import {BlockWithAlignableContents} from '@lexical/react/LexicalBlockWithAlignableContents';
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from 'lexical';
import {getAuraPublicOrigin} from 'utilities';

import {buildAuraEmbedSrc} from './auraVideoUrl';

type AuraVideoComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  videoRef: string;
}>;

const AuraVideoComponent = ({className, format, nodeKey, videoRef}: AuraVideoComponentProps) => {
  const src = buildAuraEmbedSrc(videoRef, getAuraPublicOrigin());

  return (
    <BlockWithAlignableContents className={className} format={format} nodeKey={nodeKey}>
      {src ? (
        <iframe
          src={src}
          width="100%"
          height="390"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Aura video"
        />
      ) : (
        <span data-aura-embed-disabled="true">
          Aura video embed unavailable (invalid reference)
        </span>
      )}
    </BlockWithAlignableContents>
  );
};

export type SerializedAuraVideoNode = Spread<
  {
    videoRef: string;
  },
  SerializedDecoratorBlockNode
>;

function convertAuraVideoElement(domNode: HTMLElement): null | DOMConversionOutput {
  const videoRef = domNode.getAttribute('data-lexical-aura-video');
  if (videoRef) {
    const node = $createAuraVideoNode(videoRef);
    return {node};
  }
  return null;
}

export class AuraVideoNode extends DecoratorBlockNode {
  __videoRef: string;

  static getType(): string {
    return 'aura-video';
  }

  static clone(node: AuraVideoNode): AuraVideoNode {
    return new AuraVideoNode(node.__videoRef, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedAuraVideoNode): AuraVideoNode {
    const node = $createAuraVideoNode(serializedNode.videoRef);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedAuraVideoNode {
    return {
      ...super.exportJSON(),
      type: 'aura-video',
      version: 1,
      videoRef: this.__videoRef,
    };
  }

  constructor(videoRef: string, format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__videoRef = videoRef;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('iframe');
    element.setAttribute('data-lexical-aura-video', this.__videoRef);
    element.setAttribute('width', '100%');
    element.setAttribute('height', '390');
    const src = buildAuraEmbedSrc(this.__videoRef, getAuraPublicOrigin());
    if (src) {
      element.setAttribute('src', src);
    }
    element.setAttribute('frameborder', '0');
    element.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );
    element.setAttribute('allowfullscreen', 'true');
    element.setAttribute('title', 'Aura video');
    return {element};
  }

  static importDOM(): DOMConversionMap | null {
    return {
      iframe: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-aura-video')) {
          return null;
        }
        return {
          conversion: convertAuraVideoElement,
          priority: 1,
        };
      },
    };
  }

  updateDOM(): false {
    return false;
  }

  getVideoRef(): string {
    return this.__videoRef;
  }

  getTextContent(): string {
    return `${getAuraPublicOrigin()}/video/${this.__videoRef}`;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig) {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || '',
      focus: embedBlockTheme.focus || '',
    };
    return (
      <AuraVideoComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        videoRef={this.__videoRef}
      />
    );
  }
}

export function $createAuraVideoNode(videoRef: string): AuraVideoNode {
  return new AuraVideoNode(videoRef);
}

export function $isAuraVideoNode(
  node: AuraVideoNode | LexicalNode | null | undefined
): node is AuraVideoNode {
  return node instanceof AuraVideoNode;
}
